import {type Dispatch, type RefObject, type SetStateAction, useMemo} from "react";
import {css, cx} from "@panda/css";

import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {SemanticSearchAuthOverlay} from "../../../semantic_search/components/semantic_search_auth/SemanticSearchAuthOverlay";
import {SemanticSearchConversation} from "../../../semantic_search/components/semantic_search_conversation/SemanticSearchConversation";
import {SemanticSearchQuery} from "../../../semantic_search/components/semantic_search_query/SemanticSearchQuery";
import {SemanticSearchSidebarHeader} from "../../../semantic_search/components/semantic_search_sidebar/SemanticSearchSidebarHeader";
import {SemanticSearchSidebarSkeleton} from "../../../semantic_search/components/semantic_search_sidebar/SemanticSearchSidebarSkeleton";
import {SemanticSearchClientIcon} from "../../../semantic_search/components/SemanticSearchClientIcon";
import {useSemanticSearchLocalStorage} from "../../../semantic_search/hooks/use_semantic_search_local_storage";
import {selectSemanticSearchHasConversation} from "../../../semantic_search/state/select_semantic_search_has_conversation";
import {hitAiAssistant} from "../../../semantic_search/tracking/semantic_search_gtm_events";
import {useAppSelector} from "../../../store/hooks";
import {useFloatingSemanticSearchSidebarBehavior} from "../hooks/use_floating_semantic_search_sidebar_behavior";

type Props = {
    sectionRef: RefObject<HTMLElement | null>;
    isContentLoading?: boolean;
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const SIDEBAR_WIDTH = 488;
const TOGGLE_WIDTH = 20;

export const FloatingSemanticSearchSidebar = ({sectionRef, isContentLoading = false, isOpen, setIsOpen}: Props) => {
    const hasConversation = useAppSelector(selectSemanticSearchHasConversation);

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {isLoading} = useSemanticSearchLocalStorage();

    const {shellRef, sidebarPanelRef, scrollContainerRef, mode, shellStyle, isAuthOverlayOpen} = useFloatingSemanticSearchSidebarBehavior({
        sectionRef,
        isOpen,
        setIsOpen
    });

    const showSkeleton = !isMounted || isLoading || isContentLoading;

    const positionClassName = useMemo(() => {
        if (mode === "fixed") return sidebarFixed;
        if (mode === "after-section") return sidebarAbsoluteBottom;

        return sidebarAbsoluteTop;
    }, [mode]);

    const handleToggle = () => {
        hitAiAssistant(isOpen ? "close" : "open");
        setIsOpen((currentValue) => !currentValue);
    };

    return (
        <div ref={shellRef} className={cx(sidebarShell, positionClassName, sidebarShellVisible)} style={shellStyle} suppressHydrationWarning>
            <aside
                ref={sidebarPanelRef}
                className={cx(sidebarPanel, isOpen ? sidebarPanelOpen : sidebarPanelClosed)}
                aria-hidden={!isOpen}
                suppressHydrationWarning
            >
                <button
                    type="button"
                    className={sidebarHandle}
                    onClick={handleToggle}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Zamknij panel" : "Otwórz panel"}
                >
                    {isOpen ? (
                        <ChevronRightIcon size="1.6" />
                    ) : (
                        <>
                            <ChevronLeftIcon size="1.6" />

                            <Text variant="info_txt_1" as="span" className={sidebarHandleLabel}>
                                Asystent AI
                            </Text>
                        </>
                    )}
                </button>

                <div
                    ref={scrollContainerRef}
                    className={cx(sidebarContent, (showSkeleton || !hasConversation) && sidebardContentCenter, isAuthOverlayOpen && sidebarContentLocked)}
                >
                    {showSkeleton ? (
                        <SemanticSearchSidebarSkeleton />
                    ) : (
                        <>
                            <div className={css({px: 2, flexGrow: 1})}>
                                {!hasConversation && <SemanticSearchClientIcon />}
                                <SemanticSearchSidebarHeader />
                                <SemanticSearchConversation containerRef={scrollContainerRef} />
                            </div>
                            <SemanticSearchQuery hasStickySidebar />
                        </>
                    )}
                </div>

                {!isMobile && !showSkeleton && <SemanticSearchAuthOverlay />}
            </aside>
        </div>
    );
};

const sidebarShell = css({
    display: "none",
    position: "absolute",
    top: 0,
    right: 0,
    width: `${SIDEBAR_WIDTH}px`,
    height: "100vh",
    pointerEvents: "none",
    overflow: "visible",
    transition: "opacity 0.3s ease-out, top 0.05s ease-out, height 0.05s ease-out",
    willChange: "opacity, top, height",
    opacity: 0,

    md: {
        display: "initial"
    }
});

const sidebarShellVisible = css({
    opacity: 1
});

const sidebarPositionBase = {
    right: 0,
    height: "100vh"
} as const;

const sidebarFixed = css({
    ...sidebarPositionBase,
    position: "fixed",
    top: 0
});

const sidebarAbsoluteTop = css({
    ...sidebarPositionBase,
    position: "absolute",
    top: 0
});

const sidebarAbsoluteBottom = css({
    ...sidebarPositionBase,
    position: "absolute"
});

const sidebarPanel = css({
    position: "absolute",
    top: 0,
    right: 0,
    width: `${SIDEBAR_WIDTH}px`,
    height: "100%",
    background: "var(--colors-white)",
    transition: "transform 0.3s ease",
    pointerEvents: "auto",
    overflow: "visible",
    willChange: "transform"
});

const sidebarPanelClosed = css({
    transform: "translateX(calc(100% - 16px))"
});

const sidebarPanelOpen = css({
    transform: "translateX(0)"
});

const sidebarHandle = css({
    position: "absolute",
    left: `-${TOGGLE_WIDTH - 1}px`,
    top: "80px",
    transform: "translateY(-50%)",
    width: `${TOGGLE_WIDTH}px`,
    minHeight: "44px",
    px: 1,
    py: 1,
    borderTopLeftRadius: 0.5,
    borderBottomLeftRadius: 0.5,
    border: "none",
    background: "var(--colors-white)",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2
});

const sidebarHandleLabel = css({
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    whiteSpace: "nowrap"
});

const sidebarContent = css({
    height: "100%",
    overflowY: "auto",
    pt: 2,
    minHeight: "540px",
    position: "relative",
    display: "flex",
    flexDirection: "column"
});

const sidebardContentCenter = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
});

const sidebarContentLocked = css({
    overflow: "hidden",
    pointerEvents: "none"
});
import {type Dispatch, type RefObject, type SetStateAction, useMemo} from "react";
import {css, cx} from "@panda/css";

import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {SemanticSearchAuthOverlay} from "../../../semantic_search/components/semantic_search_auth/SemanticSearchAuthOverlay";
import {SemanticSearchConversation} from "../../../semantic_search/components/semantic_search_conversation/SemanticSearchConversation";
import {SemanticSearchQuery} from "../../../semantic_search/components/semantic_search_query/SemanticSearchQuery";
import {SemanticSearchSidebarHeader} from "../../../semantic_search/components/semantic_search_sidebar/SemanticSearchSidebarHeader";
import {SemanticSearchSidebarSkeleton} from "../../../semantic_search/components/semantic_search_sidebar/SemanticSearchSidebarSkeleton";
import {SemanticSearchClientIcon} from "../../../semantic_search/components/SemanticSearchClientIcon";
import {useSemanticSearchLocalStorage} from "../../../semantic_search/hooks/use_semantic_search_local_storage";
import {selectSemanticSearchHasConversation} from "../../../semantic_search/state/select_semantic_search_has_conversation";
import {hitAiAssistant} from "../../../semantic_search/tracking/semantic_search_gtm_events";
import {useAppSelector} from "../../../store/hooks";
import {useFloatingSemanticSearchSidebarBehavior} from "../hooks/use_floating_semantic_search_sidebar_behavior";

type Props = {
    sectionRef: RefObject<HTMLElement | null>;
    isContentLoading?: boolean;
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const SIDEBAR_WIDTH = 488;
const TOGGLE_WIDTH = 20;

export const FloatingSemanticSearchSidebar = ({sectionRef, isContentLoading = false, isOpen, setIsOpen}: Props) => {
    const hasConversation = useAppSelector(selectSemanticSearchHasConversation);

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {isLoading} = useSemanticSearchLocalStorage();

    const {shellRef, sidebarPanelRef, scrollContainerRef, mode, shellStyle, isAuthOverlayOpen} = useFloatingSemanticSearchSidebarBehavior({
        sectionRef,
        isOpen,
        setIsOpen
    });

    const showSkeleton = !isMounted || isLoading || isContentLoading;

    const positionClassName = useMemo(() => {
        if (mode === "fixed") return sidebarFixed;
        if (mode === "after-section") return sidebarAbsoluteBottom;

        return sidebarAbsoluteTop;
    }, [mode]);

    const handleToggle = () => {
        hitAiAssistant(isOpen ? "close" : "open");
        setIsOpen((currentValue) => !currentValue);
    };

    return (
        <div ref={shellRef} className={cx(sidebarShell, positionClassName, sidebarShellVisible)} style={shellStyle} suppressHydrationWarning>
            <aside
                ref={sidebarPanelRef}
                className={cx(sidebarPanel, isOpen ? sidebarPanelOpen : sidebarPanelClosed)}
                aria-hidden={!isOpen}
                suppressHydrationWarning
            >
                <button
                    type="button"
                    className={sidebarHandle}
                    onClick={handleToggle}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Zamknij panel" : "Otwórz panel"}
                >
                    {isOpen ? (
                        <ChevronRightIcon size="1.6" />
                    ) : (
                        <>
                            <ChevronLeftIcon size="1.6" />

                            <Text variant="info_txt_1" as="span" className={sidebarHandleLabel}>
                                Asystent AI
                            </Text>
                        </>
                    )}
                </button>

                <div
                    ref={scrollContainerRef}
                    className={cx(sidebarContent, (showSkeleton || !hasConversation) && sidebardContentCenter, isAuthOverlayOpen && sidebarContentLocked)}
                >
                    {showSkeleton ? (
                        <SemanticSearchSidebarSkeleton />
                    ) : (
                        <>
                            <div className={css({px: 2, flexGrow: 1})}>
                                {!hasConversation && <SemanticSearchClientIcon />}
                                <SemanticSearchSidebarHeader />
                                <SemanticSearchConversation containerRef={scrollContainerRef} />
                            </div>
                            <SemanticSearchQuery hasStickySidebar />
                        </>
                    )}
                </div>

                {!isMobile && !showSkeleton && <SemanticSearchAuthOverlay />}
            </aside>
        </div>
    );
};

const sidebarShell = css({
    display: "none",
    position: "absolute",
    top: 0,
    right: 0,
    width: `${SIDEBAR_WIDTH}px`,
    height: "100vh",
    pointerEvents: "none",
    overflow: "visible",
    transition: "opacity 0.3s ease-out, top 0.05s ease-out, height 0.05s ease-out",
    willChange: "opacity, top, height",
    opacity: 0,

    md: {
        display: "initial"
    }
});

const sidebarShellVisible = css({
    opacity: 1
});

const sidebarPositionBase = {
    right: 0,
    height: "100vh"
} as const;

const sidebarFixed = css({
    ...sidebarPositionBase,
    position: "fixed",
    top: 0
});

const sidebarAbsoluteTop = css({
    ...sidebarPositionBase,
    position: "absolute",
    top: 0
});

const sidebarAbsoluteBottom = css({
    ...sidebarPositionBase,
    position: "absolute"
});

const sidebarPanel = css({
    position: "absolute",
    top: 0,
    right: 0,
    width: `${SIDEBAR_WIDTH}px`,
    height: "100%",
    background: "var(--colors-white)",
    transition: "transform 0.3s ease",
    pointerEvents: "auto",
    overflow: "visible",
    willChange: "transform"
});

const sidebarPanelClosed = css({
    transform: "translateX(calc(100% - 16px))"
});

const sidebarPanelOpen = css({
    transform: "translateX(0)"
});

const sidebarHandle = css({
    position: "absolute",
    left: `-${TOGGLE_WIDTH - 1}px`,
    top: "80px",
    transform: "translateY(-50%)",
    width: `${TOGGLE_WIDTH}px`,
    minHeight: "44px",
    px: 1,
    py: 1,
    borderTopLeftRadius: 0.5,
    borderBottomLeftRadius: 0.5,
    border: "none",
    background: "var(--colors-white)",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2
});

const sidebarHandleLabel = css({
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    whiteSpace: "nowrap"
});

const sidebarContent = css({
    height: "100%",
    overflowY: "auto",
    pt: 2,
    minHeight: "540px",
    position: "relative",
    display: "flex",
    flexDirection: "column"
});

const sidebardContentCenter = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
});

const sidebarContentLocked = css({
    overflow: "hidden",
    pointerEvents: "none"
});
